import { useState, useEffect, useRef, useCallback } from 'react';
import { Tool, getToolById, getCategoryDisplayLabel } from '../data/tools';
import { getGuideByToolId, Guide } from '../data/guides';
import { useToolbox } from '../context/ToolboxContext';
import { useChat } from '../context/ChatContext';
import { getEmbedUrl } from '../utils/videoEmbed';
import { HipaaBadge } from './HipaaBadge';

interface ToolTabProps {
  toolId: string;
}

export function ToolTab({ toolId }: ToolTabProps) {
  const tool = getToolById(toolId);
  const guide = getGuideByToolId(toolId);

  if (!tool) {
    return (
      <div className="h-full flex items-center justify-center p-8">
        <div className="text-center">
          <h3 className="text-lg font-semibold text-neutral-800 mb-2 font-heading">Tool Not Found</h3>
          <p className="text-neutral-500 text-sm">The tool you're looking for doesn't exist.</p>
        </div>
      </div>
    );
  }

  // Catalog tools (added via /admin) get a video-based guide view.
  if (!guide && (tool.videoUrl || tool.guideNotes)) {
    return <VideoGuide tool={tool} />;
  }

  if (!guide) {
    return (
      <div className="h-full flex flex-col p-8">
        <ToolHeader tool={tool} />
        <div className="flex-1 flex items-center justify-center">
          <div className="bg-amber-50 border border-amber-200 rounded-card p-8 max-w-md">
            <div className="flex items-start gap-4">
              <div className="bg-amber-100 rounded-full p-3">
                <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-amber-800 mb-2 font-heading">Setup Guide Coming Soon</h3>
                <p className="text-amber-700 text-sm mb-4">
                  We're working on a detailed step-by-step setup guide for {tool.name}.
                </p>
                <a
                  href={tool.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-amber-800 hover:text-amber-900 font-medium"
                >
                  Visit Website
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return <ToolIntegrationFlow tool={tool} guide={guide} />;
}

/** Guide view for admin-added catalog tools: embedded walkthrough video + notes. */
function VideoGuide({ tool }: { tool: Tool }) {
  const embedUrl = tool.videoUrl ? getEmbedUrl(tool.videoUrl) : null;
  const { addTool, isToolInToolbox, getStageForTool } = useToolbox();
  const canAdd = getStageForTool(tool) !== null;

  return (
    <div className="h-full flex flex-col">
      <ToolHeader tool={tool} />
      <div className="flex-1 overflow-y-auto overscroll-contain p-6">
        <div className="max-w-2xl mx-auto space-y-6">
          <div>
            <h3 className="text-xl font-semibold text-neutral-800 mb-3 font-heading">About {tool.name}</h3>
            <p className="text-neutral-600 leading-relaxed">{tool.description}</p>
          </div>

          {tool.videoUrl && (
            <div>
              <h4 className="text-sm font-medium text-neutral-700 uppercase tracking-wide mb-3">Video Walkthrough</h4>
              {embedUrl ? (
                <div className="aspect-video rounded-card overflow-hidden border border-neutral-200 shadow-card">
                  <iframe
                    src={embedUrl}
                    title={`${tool.name} setup walkthrough`}
                    className="w-full h-full"
                    allowFullScreen
                  />
                </div>
              ) : (
                <a
                  href={tool.videoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary inline-flex items-center gap-2"
                >
                  Watch The Walkthrough Video
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              )}
            </div>
          )}

          {tool.guideNotes && (
            <div className="bg-neutral-50 border border-neutral-200 rounded-card p-5">
              <h4 className="text-sm font-medium text-neutral-700 uppercase tracking-wide mb-3">Setup Notes</h4>
              <div className="whitespace-pre-wrap text-sm text-neutral-700 leading-relaxed">{tool.guideNotes}</div>
            </div>
          )}

          {(tool.bestFor || tool.pricing) && (
            <div className="grid grid-cols-2 gap-3">
              {tool.bestFor && (
                <div className="bg-neutral-50 border border-neutral-200 rounded-card p-4">
                  <p className="text-xs font-medium text-neutral-500 uppercase tracking-wide mb-1">Best For</p>
                  <p className="text-sm text-neutral-800">{tool.bestFor}</p>
                </div>
              )}
              {tool.pricing && (
                <div className="bg-neutral-50 border border-neutral-200 rounded-card p-4">
                  <p className="text-xs font-medium text-neutral-500 uppercase tracking-wide mb-1">Pricing</p>
                  <p className="text-sm text-neutral-800">{tool.pricing}</p>
                </div>
              )}
            </div>
          )}

          {tool.keyFeatures.length > 0 && (
            <div className="bg-neutral-50 border border-neutral-200 rounded-card p-5">
              <h4 className="text-sm font-medium text-neutral-700 uppercase tracking-wide mb-3">What It Does</h4>
              <ul className="space-y-2">
                {tool.keyFeatures.map((feature, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm text-neutral-700">
                    <svg className="w-4 h-4 text-primary-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {canAdd && !isToolInToolbox(tool.id) && (
            <button
              onClick={() => addTool(tool)}
              className="w-full py-3 px-4 rounded-btn font-medium bg-primary-600 text-white hover:bg-primary-700 transition-colors"
            >
              Add To Toolbox
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function ToolHeader({ tool }: { tool: NonNullable<ReturnType<typeof getToolById>> }) {
  return (
    <div className="border-b border-neutral-200 px-6 py-4 bg-neutral-50">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h2 className="text-2xl font-bold text-neutral-800 font-heading">{tool.name}</h2>
          <span className="badge-primary">
            {getCategoryDisplayLabel(tool)}
          </span>
          <HipaaBadge compliant={tool.hipaaCompliant} />
        </div>
        <a
          href={tool.website}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-primary inline-flex items-center gap-2"
        >
          Visit Website
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
          </svg>
        </a>
      </div>
    </div>
  );
}

function ToolIntegrationFlow({ tool, guide }: { tool: NonNullable<ReturnType<typeof getToolById>>; guide: Guide }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const { addTool, isToolInToolbox } = useToolbox();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const totalSteps = guide.steps.length;

  // Load progress from localStorage. Clamp the restored step to the guide's
  // current length — a guide shortened since the progress was saved (or a
  // tampered/stale key) must not index past the end of guide.steps.
  useEffect(() => {
    const storageKey = `athen-tool-progress-${tool.id}`;
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const progress = JSON.parse(saved);
        const savedStep = Number(progress?.lastActiveStep) || 0;
        setCurrentStep(Math.min(Math.max(0, savedStep), totalSteps));
        const savedCompleted = Array.isArray(progress?.completedSteps) ? progress.completedSteps : [];
        setCompletedSteps(savedCompleted.filter((n: unknown) => typeof n === 'number' && n >= 0 && n <= totalSteps));
      }
    } catch (e) {
      console.error('Failed to load tool progress:', e);
    }
  }, [tool.id, totalSteps]);

  // Save progress to localStorage
  useEffect(() => {
    const storageKey = `athen-tool-progress-${tool.id}`;
    try {
      localStorage.setItem(
        storageKey,
        JSON.stringify({
          lastActiveStep: currentStep,
          completedSteps,
        })
      );
    } catch (e) {
      console.error('Failed to save tool progress:', e);
    }
  }, [tool.id, currentStep, completedSteps]);

  const scrollToTop = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    if (currentStep > 0) {
      const timer = setTimeout(() => {
        scrollToTop();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [currentStep]);

  const nextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const allStepsComplete = currentStep === totalSteps;

  return (
    <div className="h-full flex flex-col">
      <ToolHeader tool={tool} />

      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto overscroll-contain">
        {currentStep === 0 ? (
          <ToolOverview
            tool={tool}
            guide={guide}
            onStartSetup={() => setCurrentStep(1)}
          />
        ) : (
          <SetupStep
            step={guide.steps[currentStep - 1]}
            stepNumber={currentStep}
            onNext={currentStep < totalSteps ? nextStep : undefined}
            onPrev={prevStep}
            isLastStep={currentStep === totalSteps}
            allStepsComplete={allStepsComplete}
            tool={tool}
            isInToolbox={isToolInToolbox(tool.id)}
            onAddToToolbox={() => addTool(tool)}
          />
        )}
      </div>
    </div>
  );
}

function ToolOverview({
  tool,
  guide,
  onStartSetup,
}: {
  tool: NonNullable<ReturnType<typeof getToolById>>;
  guide: Guide;
  onStartSetup: () => void;
}) {
  return (
    <div className="p-6">
      <div className="max-w-2xl mx-auto">
        {/* Overview */}
        <div className="mb-6">
          <h3 className="text-xl font-semibold text-neutral-800 mb-3 font-heading">About {tool.name}</h3>
          <p className="text-neutral-600 leading-relaxed">{guide.overview}</p>
        </div>

        {/* Video walkthrough (from the admin catalog), when one exists */}
        {tool.videoUrl && getEmbedUrl(tool.videoUrl) && (
          <div className="mb-6">
            <h4 className="text-sm font-medium text-neutral-700 uppercase tracking-wide mb-3">Video Walkthrough</h4>
            <div className="aspect-video rounded-card overflow-hidden border border-neutral-200 shadow-card">
              <iframe
                src={getEmbedUrl(tool.videoUrl)!}
                title={`${tool.name} setup walkthrough`}
                className="w-full h-full"
                allowFullScreen
              />
            </div>
          </div>
        )}

        {/* Key info cards */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <div className="bg-neutral-50 border border-neutral-200 rounded-card p-4">
            <p className="text-xs font-medium text-neutral-500 uppercase tracking-wide mb-1">Best For</p>
            <p className="text-sm text-neutral-800">{tool.bestFor}</p>
          </div>
          <div className="bg-neutral-50 border border-neutral-200 rounded-card p-4">
            <p className="text-xs font-medium text-neutral-500 uppercase tracking-wide mb-1">Pricing</p>
            <p className="text-sm text-neutral-800">{tool.pricing}</p>
          </div>
          <div className="bg-neutral-50 border border-neutral-200 rounded-card p-4">
            <p className="text-xs font-medium text-neutral-500 uppercase tracking-wide mb-1">Setup Time</p>
            <p className="text-sm text-neutral-800">{guide.timeEstimate}</p>
          </div>
          <div className="bg-neutral-50 border border-neutral-200 rounded-card p-4">
            <p className="text-xs font-medium text-neutral-500 uppercase tracking-wide mb-1">HIPAA</p>
            <p className="text-sm text-neutral-800">
              {tool.hipaaCompliant ? 'HIPAA Compliant' : 'Not HIPAA Compliant'}
            </p>
          </div>
        </div>

        {/* Key features */}
        <div className="bg-neutral-50 border border-neutral-200 rounded-card p-6 mb-6">
          <h4 className="text-sm font-medium text-neutral-700 uppercase tracking-wide mb-3">What It Does</h4>
          <ul className="space-y-2">
            {tool.keyFeatures.map((feature, index) => (
              <li key={index} className="flex items-start gap-2 text-sm text-neutral-700">
                <svg className="w-4 h-4 text-primary-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                {feature}
              </li>
            ))}
          </ul>
        </div>

        {/* Setup steps preview */}
        <div className="mb-6">
          <h4 className="text-sm font-medium text-neutral-700 uppercase tracking-wide mb-3">Setup Guide ({guide.steps.length} steps)</h4>
          <div className="space-y-2">
            {guide.steps.map((step, index) => (
              <div key={index} className="flex items-center gap-3 text-sm text-neutral-600">
                <span className="w-6 h-6 rounded-full bg-neutral-200 text-neutral-600 flex items-center justify-center text-xs font-medium flex-shrink-0">
                  {index + 1}
                </span>
                {step.title}
              </div>
            ))}
          </div>
        </div>

        {/* Start button */}
        <button
          onClick={onStartSetup}
          className="w-full py-3 px-4 rounded-btn font-medium bg-primary-600 text-white hover:bg-primary-700 transition-colors"
        >
          Start Setup Guide
        </button>
      </div>
    </div>
  );
}

function SelectionPopup({
  toolName,
  stepNumber,
  stepTitle,
  containerRef,
}: {
  toolName: string;
  stepNumber: number;
  stepTitle: string;
  containerRef: React.RefObject<HTMLDivElement | null>;
}) {
  const { setPendingInput } = useChat();
  const [popup, setPopup] = useState<{ text: string; top: number; left: number } | null>(null);
  const popupRef = useRef<HTMLButtonElement>(null);

  const handleMouseUp = useCallback(() => {
    const selection = window.getSelection();
    if (!selection || selection.isCollapsed || !containerRef.current) {
      return;
    }

    const text = selection.toString().trim();
    if (!text) {
      return;
    }

    const range = selection.getRangeAt(0);
    const rect = range.getBoundingClientRect();
    const containerRect = containerRef.current.getBoundingClientRect();

    setPopup({
      text: text.length > 200 ? text.slice(0, 200) + '…' : text,
      top: rect.top - containerRect.top - 36,
      left: rect.left - containerRect.left + rect.width / 2,
    });
  }, [containerRef]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    container.addEventListener('mouseup', handleMouseUp);
    return () => container.removeEventListener('mouseup', handleMouseUp);
  }, [containerRef, handleMouseUp]);

  useEffect(() => {
    if (!popup) return;

    const handleMouseDown = (e: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
        setPopup(null);
      }
    };

    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [popup]);

  if (!popup) return null;

  return (
    <button
      ref={popupRef}
      onClick={() => {
        const message = `Regarding ${toolName} setup (Step ${stepNumber}: ${stepTitle}): "${popup.text}" — can you explain this?`;
        setPendingInput(message);
        setPopup(null);
        window.getSelection()?.removeAllRanges();
      }}
      className="absolute z-10 px-3 py-1.5 bg-primary-600 text-white text-xs font-medium rounded-pill shadow-lg hover:bg-primary-700 transition-colors whitespace-nowrap -translate-x-1/2"
      style={{ top: popup.top, left: popup.left }}
    >
      Ask About This
    </button>
  );
}

function SetupStep({
  step,
  stepNumber,
  onNext,
  onPrev,
  isLastStep,
  allStepsComplete,
  tool,
  isInToolbox,
  onAddToToolbox,
}: {
  step: { title: string; content: string; tip?: string };
  stepNumber: number;
  onNext?: () => void;
  onPrev: () => void;
  isLastStep: boolean;
  allStepsComplete: boolean;
  tool: NonNullable<ReturnType<typeof getToolById>>;
  isInToolbox: boolean;
  onAddToToolbox: () => void;
}) {
  const contentRef = useRef<HTMLDivElement>(null);

  return (
    <div className="p-6">
      <div className="max-w-3xl mx-auto">
        {/* Step Header */}
        <div className="flex items-center gap-4 mb-6">
          <div
            className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold flex-shrink-0 ${
              stepNumber === 1
                ? 'bg-primary-600 text-white'
                : 'bg-neutral-200 text-neutral-600'
            }`}
          >
            {stepNumber}
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-semibold text-neutral-800 font-heading">{step.title}</h3>
          </div>
        </div>

        {/* Step Content */}
        <div ref={contentRef} className="relative bg-white border border-neutral-200 rounded-card p-6 mb-6">
          <SelectionPopup
            toolName={tool.name}
            stepNumber={stepNumber}
            stepTitle={step.title}
            containerRef={contentRef}
          />
          <div className="prose prose-neutral prose-sm max-w-none">
            <div className="whitespace-pre-wrap text-neutral-700 leading-relaxed">{step.content}</div>
          </div>

          {/* Screenshot Placeholder */}
          <div className="mt-6 bg-neutral-100 border-2 border-dashed border-neutral-300 rounded-card p-12 text-center">
            <svg className="w-12 h-12 text-neutral-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <p className="text-sm text-neutral-500">Screenshot Placeholder For Step</p>
          </div>

          {step.tip && (
            <div className="mt-6 bg-blue-50 border border-blue-200 rounded-card p-4">
              <div className="flex gap-3">
                <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <div>
                  <span className="font-medium text-blue-800">Tip: </span>
                  <span className="text-blue-700">{step.tip}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Completion Step */}
        {isLastStep && allStepsComplete && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-card p-6 mb-6">
            <div className="flex items-start gap-4">
              <div className="bg-emerald-100 rounded-full p-3">
                <svg className="w-6 h-6 text-emerald-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="flex-1">
                <h4 className="text-lg font-semibold text-emerald-800 font-heading mb-2">Setup Complete!</h4>
                <p className="text-emerald-700 text-sm mb-4">
                  You've successfully set up {tool?.name}. You can now start using it in your toolbox.
                </p>
                {!isInToolbox && tool && (
                  <button
                    onClick={onAddToToolbox}
                    className="bg-emerald-600 text-white px-4 py-2 rounded-btn hover:bg-emerald-700 transition-colors text-sm font-medium"
                  >
                    Add To Toolbox
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between gap-4">
          <button
            onClick={onPrev}
            disabled={stepNumber === 1}
            className={`px-4 py-2 rounded-btn font-medium transition-colors ${
              stepNumber === 1
                ? 'bg-neutral-100 text-neutral-400 cursor-not-allowed'
                : 'bg-neutral-200 text-neutral-700 hover:bg-neutral-300'
            }`}
          >
            Previous
          </button>

          {onNext && (
            <button
              onClick={onNext}
              className="btn-primary px-6"
            >
              Next Step
            </button>
          )}

        </div>
      </div>
    </div>
  );
}
